/*
 * Actions for the [reporters store]{link module:store/reporters}
 *
 * @module store/reporters/actions
 */

import * as apiReporters from '@/api/reporters'

import { MEDIUM_FLAG, ReporterConfig } from '@/store/modules/reporters'
import { EmsGroup } from '@/store/modules/energy_management_system_groups'
import { Ems } from '@/store/modules/energy_management_systems'

const REPORTER_PARSE_TYPE_MAP = {
  'Reporters::LogEventProber': 'logEvent',
  'Reporters::SensorProber': 'sensor'
}

/**
 * Action to reset the reporters
 *
 * @function
 *
 */
export function resetStore({ commit }) {
  commit('RESET_REPORTERS')
  return true
}

/**
 * API call to POST create a new Reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {array} params.id is the id of the reporter that is to be created
 *
 * @return {promise} which resolves to the report (json:api data-response)
 */
export async function createReporter({ state }, { ctxt = 'customer', id }) {
  const reporter = state[id]

  return apiReporters.createReporter({ ctxt, reporter })
}

/**
 * Action to create a local empty reporter for an EMS
 *
 * dispatches a GET for an EMS to get the ems_access_group_id
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.id is the id of the new reporter
 * @param {integer} params.emsId
 */
export async function createReporterForEms({ commit, dispatch }, { ctxt = 'customer', id, emsId }) {
  const ems = await dispatch(
    'energyManagementSystems/getEms',
    { emsId, fields: ['name', 'ems_access_group'], ctxt },
    { root: true }
  )

  const groupId = parseInt(ems?.relationships?.emsAccessGroup?.data?.id)

  commit('CREATE_REPORTER', { id, emsId, groupId })
}

/**
 * API call to PATCH a reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.id is the id of the updating reporter
 */
export async function updateReporter({ state }, { ctxt = 'customer', reporterId }) {
  return apiReporters.updateReporter({ ctxt, reporter: state[reporterId] })
}

/**
 * API call to GET all reporters for the current user aswell as all EMSs for the reportergroups
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 *
 * @return {Promise}
 */
export async function getReportersForCurrentUser({ commit }, { ctxt = 'customer' }) {
  const rsp = await apiReporters.getReportersForCurrentUser({ ctxt })

  let groupIds = []

  for (const reporter of rsp.payload.data) {
    groupIds.push(parseInt(reporter.relationships.group.data.id))

    commit('CREATE_REPORTER', {
      id: parseInt(reporter.id),
      groupId: parseInt(reporter.relationships.group.data.id),
      name: reporter.attributes.name,
      type: REPORTER_PARSE_TYPE_MAP[reporter.attributes.type],
      notifyOnConditionNoLongerFulfilled: reporter.attributes.notifyOnConditionNoLongerFulfilled,
      currentStatus: reporter.attributes.currentStatus,
      disabled: reporter.attributes.disabled,
      config: new ReporterConfig(reporter.attributes.config)
    })
  }

  groupIds = [...new Set(groupIds)].sort().join(',')
  const rspEmss = await apiReporters.getEmssForReporterGroups({ ctxt, groupIds })
  if (!rspEmss.payload.data.length) return

  for (const ems of rspEmss.payload.data) {
    const id = parseInt(ems.id)

    commit('energyManagementSystems/SET_EMS', new Ems({ id, name: ems.attributes.name }), { root: true })

    for (const g of ems.relationships.groups.data) {
      commit('energyManagementSystems/ADD_EMS_TO_GROUP', { emsId: id, groupId: parseInt(g.id) }, { root: true })
    }
  }

  for (const group of rspEmss.payload.included) {
    commit(
      'energyManagementSystemGroups/SET_EMS_GROUP',
      new EmsGroup({ id: group.id, name: group.attributes.name ?? group.attributes.type ?? 'Groups::EmsAccessGroup' }),
      { root: true }
    )
  }
}

/**
 * API call to GET all reporters for an EMS
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.emsId
 */
export async function getReporters({ commit, dispatch }, { ctxt = 'customer', emsId }) {
  const ems = await dispatch(
    'energyManagementSystems/getEms',
    { emsId, fields: ['name', 'ems_access_group'], ctxt },
    { root: true }
  )

  const groupId = ems?.relationships?.emsAccessGroup?.data?.id

  const rsp = await apiReporters.getReportersForGroup({ ctxt, groupId })
  for (const reporter of rsp.payload.data) {
    commit('CREATE_REPORTER', {
      id: parseInt(reporter.id),
      name: reporter.attributes.name,
      type: REPORTER_PARSE_TYPE_MAP[reporter.attributes.type],
      notifyOnConditionNoLongerFulfilled: reporter.attributes.notifyOnConditionNoLongerFulfilled,
      currentStatus: reporter.attributes.currentStatus,
      disabled: reporter.attributes.disabled,
      subscribed: reporter?.meta?.subscribed,
      config: new ReporterConfig(reporter.attributes.config)
    })
  }
}

/**
 * API call to GET a reporter by id
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.reporterId
 *
 * @return {boolean} return writable, which shows if the current user is allowed to change the reporter
 */
export async function getReporter({ commit, rootState }, { ctxt = 'customer', reporterId }) {
  const rsp = await apiReporters.getReporter({ ctxt, reporterId })
  const reporter = rsp.payload.data

  const users = parseUsers({
    userData: reporter.relationships.users.data,
    rootState
  })

  commit('CREATE_REPORTER', {
    id: parseInt(reporter.id),
    name: reporter.attributes.name,
    type: REPORTER_PARSE_TYPE_MAP[reporter.attributes.type],
    notifyOnConditionNoLongerFulfilled: reporter.attributes.notifyOnConditionNoLongerFulfilled,
    groupId: parseInt(reporter.relationships?.group?.data?.id),
    currentStatus: reporter.attributes.currentStatus,
    disabled: reporter.attributes.disabled,
    config: new ReporterConfig(reporter.attributes.config),
    users
  })

  return reporter?.meta?.writeable
}

/**
 * API call to PATCH the medium of a user<->reporter relation
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 * @param {string} params.medium {asEmail|asFcm}
 * @param {boolean} params.value the new value of the medium
 *
 */
export async function changeMedium({ state, commit }, { ctxt = 'customer', reporterId, userId, medium, value }) {
  const user = state?.[reporterId]?.users?.[userId]
  if (!user) return

  const vals = Object.assign({}, user)
  vals[medium] = value

  let mediumVal = 0
  for (const mediumType in MEDIUM_FLAG) {
    if (!vals[mediumType]) continue

    mediumVal |= MEDIUM_FLAG[mediumType]
  }

  await apiReporters.changeMedium({ ctxt, reporterId, mediumVal })

  commit('CHANGE_MEDIUM', { reporterId, userId, medium, value })
}

/**
 * API call to PATCH a user into the reporter<->users membership
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 *
 */
export async function subscribe({ commit, rootState }, { ctxt = 'customer', reporterId, userId }) {
  await apiReporters.subscribe({ ctxt, reporterId })

  const user = {}
  user.id = parseInt(userId)
  user.email = rootState.users.users?.[user.id]?.email

  for (const medium in MEDIUM_FLAG) {
    user[medium] = false
  }

  commit('SUBSCRIBE', { reporterId, user })
}

/**
 * API call to PATCH a user out of the reporter<->users membership
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the API request.
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 *
 */
export async function unsubscribe({ commit }, { ctxt = 'customer', reporterId, userId }) {
  await apiReporters.unsubscribe({ ctxt, reporterId })

  commit('UNSUBSCRIBE', { reporterId, userId })
}

/**
 * Helperfunction to map the store users datastructure into a userId => mediumValue structure
 */
const parseUsers = ({ userData, rootState }) => {
  const users = {}
  for (const uData of userData) {
    const user = {}
    user.id = parseInt(uData.id)
    user.email = rootState.users.users?.[user.id]?.email

    for (const medium in MEDIUM_FLAG) {
      user[medium] = !!(uData.meta.medium & MEDIUM_FLAG[medium])
    }
    users[user.id] = user
  }

  return users
}
